<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import DebugOverlay from './components/DebugOverlay.vue'
import MediaStage from './components/MediaStage.vue'
import { useNightlyReload } from '@/composables/useNightlyReload'
import { usePlaylist } from '@/composables/usePlaylist'
import type { PlayerSnapshot } from '@/composables/usePlayer'
import { useSelfUpdate } from '@/composables/useSelfUpdate'
import { readRuntimeConfig } from '@/config'
import { logger } from '@/lib/log'

const DEBUG_REFRESH_MS = 500
const STATUS_INTERVAL_MS = 1_000
/** A slow first load should stay black rather than flash a message. */
const EMPTY_GRACE_MS = 8_000

const config = readRuntimeConfig(window.location.href, {
  mediaBase: import.meta.env.VITE_MEDIA_BASE,
  aspect: __STAGE_ASPECT__,
})
const debug = ref(config.debug)

const { playlist, error, lastLoadedAt, start, stop } = usePlaylist(config)
const selfUpdate = useSelfUpdate(config.versionPollMs)
const nightlyReload = useNightlyReload(config.nightlyReloadAt)

const stage = ref<InstanceType<typeof MediaStage> | null>(null)
const snapshot = ref<PlayerSnapshot | null>(null)
const debugTick = ref(0)
const statusMessage = ref<string | null>(null)
let debugTimer = 0
let statusTimer = 0
let emptySince: number | null = null

function refreshStatus(): void {
  if (playlist.value !== null && (stage.value?.hasContent ?? false)) {
    emptySince = null
    statusMessage.value = null
    return
  }
  emptySince ??= Date.now()
  if (Date.now() - emptySince < EMPTY_GRACE_MS) return
  statusMessage.value =
    playlist.value === null ? 'Ingen innhold tilgjengelig' : 'Ingen spillbart innhold'
}

function refreshDebug(): void {
  snapshot.value = stage.value?.snapshot() ?? null
  debugTick.value += 1
}

function startDebugPolling(): void {
  window.clearInterval(debugTimer)
  if (!debug.value) {
    snapshot.value = null
    return
  }
  refreshDebug()
  debugTimer = window.setInterval(refreshDebug, DEBUG_REFRESH_MS)
}

/** Lets someone with only a keyboard diagnose a screen on site. */
function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'd' && event.key !== 'D') return
  debug.value = !debug.value
  startDebugPolling()
}

onMounted(() => {
  logger.info(
    `Starting playlist "${config.playlistName}" from ${config.mediaBase} (build ${__BUILD_ID__})`,
  )
  start()
  selfUpdate.start()
  nightlyReload.start()
  window.addEventListener('keydown', onKeydown)
  statusTimer = window.setInterval(refreshStatus, STATUS_INTERVAL_MS)
  startDebugPolling()
})

onBeforeUnmount(() => {
  stop()
  selfUpdate.stop()
  nightlyReload.stop()
  window.removeEventListener('keydown', onKeydown)
  window.clearInterval(debugTimer)
  window.clearInterval(statusTimer)
})
</script>

<template>
  <MediaStage ref="stage" :config="config" :playlist="playlist" />

  <p v-if="statusMessage" class="status">{{ statusMessage }}</p>

  <DebugOverlay
    v-if="debug"
    :config="config"
    :playlist="playlist"
    :snapshot="snapshot"
    :playlist-error="error"
    :last-loaded-at="lastLoadedAt"
    :stage-box="stage?.stageBox ?? null"
    :tick="debugTick"
  />
</template>

<style scoped>
.status {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.34);
  font-size: clamp(16px, 2.2vmin, 28px);
  letter-spacing: 0.04em;
}
</style>
